import { Button } from '@/components/ui/button';
import { useCreatePortalSession } from '@/features/billing/api/create-portal-session';
import { useGetSubscription } from '@/features/billing/api/get-subscription/get-subscription.hook';
import { ROUTES } from '@/lib/route-paths';
import { useSession } from '@/lib/session';
import { webAppUrl } from '@/lib/web-app-origin';
import { Link } from '@tanstack/react-router';
import {
  type CSSProperties,
  type ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { BillingRecoveryContext } from './billing-recovery-context';
import { LapsedAdsNotice } from './lapsed-ads-notice';

/** A separate viewport row keeps the banner clear of fixed app chrome on web/native. */
export function BillingRecoveryFrame({ children }: { children: ReactNode }) {
  const { data: session } = useSession();
  const { billingIssue } = useGetSubscription({
    enabled: Boolean(session?.user?.organizationId),
  });
  const visible = Boolean(billingIssue);
  const header = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  const { openPortal, isOpening: opening } = useCreatePortalSession();
  const canManage =
    session?.user?.orgRole === 'owner' || session?.user?.orgRole === 'admin';
  const needsPayment =
    billingIssue &&
    ['past_due', 'unpaid', 'incomplete'].includes(billingIssue.status);

  useLayoutEffect(() => {
    const element = header.current;
    if (!visible || !element) {
      setHeight(0);
      return;
    }
    const measure = () => setHeight(element.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [visible]);

  const managePayment = () => {
    if (opening) return;
    openPortal({
      returnUrl: webAppUrl(window.location.pathname + window.location.search),
      flow: 'payment_recovery',
    });
  };

  const style = visible
    ? ({
        '--billing-banner-height': `${height}px`,
        '--app-viewport-height': `calc(100svh - ${height}px)`,
      } as CSSProperties)
    : undefined;

  return (
    <BillingRecoveryContext.Provider value={visible ? billingIssue : null}>
      <div
        className={visible ? 'flex h-dvh flex-col overflow-hidden' : 'contents'}
        style={style}
      >
        {visible && billingIssue ? (
          <div
            ref={header}
            style={{
              fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
            }}
            role="status"
            data-billing-banner=""
            className="relative z-20 shrink-0 border-b border-black/10 bg-[#b4232a] px-4 text-white pt-[env(safe-area-inset-top,0px)]"
          >
            <div className="mx-auto flex min-h-9 w-fit max-w-full items-center justify-center gap-3 py-1 text-[13px] tracking-[-0.01em] sm:gap-4">
              <p className="text-center font-semibold leading-[18px]">
                {needsPayment
                  ? billingIssue.requiresVerification
                    ? 'Your subscription payment needs more verification.'
                    : 'Your subscription payment has failed.'
                  : billingIssue.title}
              </p>
              {canManage ? (
                needsPayment ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="h-6 shrink-0 rounded-full border border-white/25 bg-white/10 px-3.5 text-[12px] font-medium tracking-normal text-white shadow-none hover:bg-white/20 hover:text-white"
                    onClick={() => void managePayment()}
                    disabled={opening}
                  >
                    {opening ? 'Opening…' : 'Manage payments'}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="h-6 shrink-0 rounded-full border border-white/25 bg-white/10 px-3.5 text-[12px] font-medium tracking-normal text-white shadow-none hover:bg-white/20 hover:text-white"
                    asChild
                  >
                    <Link to={ROUTES.settingsBilling}>Manage payments</Link>
                  </Button>
                )
              ) : (
                <p className="text-xs">
                  Ask your owner or admin to manage payments.
                </p>
              )}
            </div>
            <LapsedAdsNotice enabled={billingIssue.status === 'canceled'} />
          </div>
        ) : null}
        {/* Keep this untransformed: fixed mobile tabs must stay anchored to the viewport. */}
        <div
          className={
            visible ? 'relative min-h-0 flex-1 overflow-auto' : 'contents'
          }
          data-billing-content={visible ? '' : undefined}
        >
          {children}
        </div>
      </div>
    </BillingRecoveryContext.Provider>
  );
}
